import JSZip from "jszip";
import security from "@/main";
import Core from "@/modules/core/service/Core";
import { getApiBaseUrl } from "@/config";
import { MEDIA } from "@/modules/img/components/service/Folders";
import Img from "@/modules/img/components/service/Img";
import { pickRendition } from "@/modules/img/components/service/Renditions";

const EXTENSIONS = {
    "image/jpeg": "jpg", "image/png": "png", "image/gif": "gif", "image/webp": "webp",
    "application/pdf": "pdf", "image/heic": "heic"
};

const withExtension = (name, contentType) => {
    const extension = EXTENSIONS[contentType];
    return !extension || name.toLowerCase().endsWith("." + extension) ? name : name + "." + extension;
};

/** Makes the names in a ZIP unique: a second "a.jpg" becomes "a (2).jpg". */
const uniqueName = (used, name) => {
    let candidate = name;
    for (let n = 2; used.has(candidate); n++) {
        const dot = name.lastIndexOf(".");
        candidate = dot > 0 ? name.slice(0, dot) + " (" + n + ")" + name.slice(dot) : name + " (" + n + ")";
    }
    used.add(candidate);
    return candidate;
};

/** Media service folder: loads the largest viewable rendition of each image and zips them. */
async function zipMedia (images) {
    const zip = new JSZip();
    const used = new Set();
    for (const image of images) {
        const rendition = pickRendition(image.renditions, "full")
            || (image.renditions || []).find(r => r.name === "original");
        if (!rendition) {
            continue;
        }

        const response = await fetch(getApiBaseUrl() + rendition.url, { method: "GET", headers: security.jsonHeader() });
        if (!response.ok) {
            return Promise.reject(response);
        }
        const name = withExtension(String(image.name || image.fileName || image.id), rendition.contentType);
        zip.file(uniqueName(used, name), await response.blob());
    }
    return zip.generateAsync({ type: "blob" });
}

/** Image Service folder: prepares the folder's images on the server and loads its ZIP. */
async function zipLegacy (siteId, folder, folderName, original) {
    const download = await Core.method.get("/api/image/unit/{0}/export", siteId);
    const legacyId = folder.sources[0].id;
    const exported = download.folders.find(f => f.id === legacyId) || download.folders.find(f => f.name === folder.name);
    if (!exported) {
        return null;
    }

    for (const image of exported.images) {
        const request = Core.data.clone(image);
        request.zipPath = folderName + "/" + image.name;
        if (!original) {
            request.url = null;
        }
        await Core.method.putVoid("/api/image/export/{0}", request, download.id);
    }

    return new Promise((resolve, reject) => {
        Core.transfer.load("/api/image/export/file/{0}".replace("{0}", download.id), resolve, reject);
    });
}

export default {
    /**
     * ZIP of all images of a folder, as an object URL, or null if the folder has no images.
     * [images]: the folder's images as shown (needed for media service folders).
     */
    async zip (siteId, folder, folderName, images, original) {
        if (folder.sources[0].source === MEDIA) {
            return images.length > 0 ? URL.createObjectURL(await zipMedia(images)) : null;
        }
        return images.length > 0 ? zipLegacy(siteId, folder, folderName, original) : null;
    },

    /** Downloads one image of a folder's images (full size). */
    async image (image) {
        const url = Img.imageUrl(image, "full");
        if (!url) {
            return Promise.reject();
        }
        const response = await fetch(url, { method: "GET", headers: security.jsonHeader() });
        if (!response.ok) {
            return Promise.reject(response);
        }
        const blob = await response.blob();
        const name = withExtension(String(image.name || image.fileName || image.id), blob.type);
        this.save(URL.createObjectURL(blob), name);
    },

    /** Saves [url] (an object URL) as a file called [filename]. */
    save (url, filename) {
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        link.click();
    }
};
